import { useEffect, useRef } from 'react'
import type { Point, Rect } from './geometry'

// Distance from a point to the nearest edge of a rectangle. 0 if the point is inside.
// Lets us anchor drag halos + drop ripples to item silhouettes rather than single
// centers — so a big item gets the halo around its whole edge, not buried inside.
function distanceFromRectEdge(px: number, py: number, r: Rect): number {
  const dx = Math.max(r.x - px, 0, px - (r.x + r.w))
  const dy = Math.max(r.y - py, 0, py - (r.y + r.h))
  return Math.sqrt(dx * dx + dy * dy)
}

// Ported and slimmed from moodboard's DotGridLayer. Three effects:
//   1. Cursor magnetism — dots within FALLOFF brighten + grow toward the pointer.
//   2. Drag halo — dots around any currently-dragging item brighten.
//   3. Drop ripple — a propagating ring of brightness fired when an item lands.
// Skipped from the moodboard version (for now): gravitational warp (the most expensive
// effect; subtle compared to the others — easy to add back if we miss it).
//
// Drawn directly on a 2D <canvas> element behind the world layer. The canvas applies
// the same translate+scale transform as the world div so we can loop in world
// coordinates like the source did.

const DOT_SPACING = 24
const DOT_BASE_RADIUS = 0.9
// Minimum on-screen spacing between dots. At zoom-out, we double the world-spacing
// until each dot is at least this many screen pixels from its neighbours. Keeps the
// per-frame dot count bounded so deep zoom-outs don't tank the framerate.
const MIN_SCREEN_SPACING = 12

const CURSOR_FALLOFF_WORLD = 140
const DOT_PEAK_RADIUS = 1.5
const DOT_PEAK_ALPHA = 0.5

const PULSE_PERIOD_MS = 4200
const PULSE_AMPLITUDE = 0.04

const RIPPLE_LIFETIME_MS = 850
const RIPPLE_SPEED_PX_PER_MS = 0.6
const RIPPLE_WIDTH_WORLD = 36
const RIPPLE_PEAK_ALPHA = 0.55
const RIPPLE_PEAK_RADIUS_BOOST = 1.3

const DRAG_HALO_FALLOFF_WORLD = 160
const DRAG_HALO_PEAK_ALPHA_BOOST = 0.45
const DRAG_HALO_PEAK_RADIUS_BOOST = 0.8

// Module-scope ripple queue — components can fire `emitDotRipple` from anywhere
// without prop-drilling. The grid prunes expired entries each frame.
// Ripples carry the rect of the item that triggered them so the ring propagates from
// the item's edge instead of its centre. Big items still get a visible ripple.
type Ripple = Rect & { startTime: number }
export function emitDotRipple(rect: Rect) {
  ripples.push({ ...rect, startTime: performance.now() })
}
const ripples: Ripple[] = []

type Props = {
  scale: number
  offset: Point
  viewport: { width: number; height: number }
  // Cursor in WORLD coordinates. Null when off-canvas.
  cursorWorld: Point | null
  // Bounding rects (world coords) of items currently being dragged. The halo is
  // computed from distance to the rect edge, so big items get a visible halo
  // around their whole silhouette instead of just from a single centre point.
  dragHalos: Rect[]
}

export function DotGrid({ scale, offset, viewport, cursorWorld, dragHalos }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Capture latest props in a ref so the rAF loop reads fresh values without restarting.
  const stateRef = useRef({ scale, offset, viewport, cursorWorld, dragHalos })
  stateRef.current = { scale, offset, viewport, cursorWorld, dragHalos }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    let raf = 0

    const draw = () => {
      const { scale, offset, viewport, cursorWorld, dragHalos } = stateRef.current
      // Resolve theme each frame from the html class — handles 'system' theme + live
      // theme toggles without needing a context subscription.
      const dark = document.documentElement.classList.contains('dark')
      if (viewport.width === 0 || viewport.height === 0) {
        raf = requestAnimationFrame(draw)
        return
      }

      // Make sure the canvas backing store matches viewport × dpr.
      const cssW = viewport.width
      const cssH = viewport.height
      if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
        canvas.width = cssW * dpr
        canvas.height = cssH * dpr
        canvas.style.width = `${cssW}px`
        canvas.style.height = `${cssH}px`
      }

      // Clear in device pixels, then set up a transform that combines dpr with the
      // world's translate + scale so we can draw in world coords.
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * offset.x, dpr * offset.y)

      const now = performance.now()

      const left = -offset.x / scale
      const top = -offset.y / scale
      const right = (viewport.width - offset.x) / scale
      const bottom = (viewport.height - offset.y) / scale

      // Adaptive world-spacing: double until on-screen spacing meets the minimum.
      // Caps per-frame dot count when the user zooms out far.
      let spacing = DOT_SPACING
      while (spacing * scale < MIN_SCREEN_SPACING) spacing *= 2

      const startX = Math.floor(left / spacing) * spacing
      const endX = Math.ceil(right / spacing) * spacing
      const startY = Math.floor(top / spacing) * spacing
      const endY = Math.ceil(bottom / spacing) * spacing

      const phase = ((now % PULSE_PERIOD_MS) / PULSE_PERIOD_MS) * Math.PI * 2
      const pulse = Math.sin(phase) * PULSE_AMPLITUDE
      // Light mode needs more alpha to read against the brighter background; dark mode
      // is gentler so the dots feel ambient rather than chrome.
      const baseAlpha = dark ? 0.18 : 0.22
      const rgb = dark ? '255,255,255' : '0,0,0'

      // Prune expired ripples.
      for (let i = ripples.length - 1; i >= 0; i--) {
        if (now - ripples[i]!.startTime >= RIPPLE_LIFETIME_MS) {
          ripples.splice(i, 1)
        }
      }

      const falloffSq = CURSOR_FALLOFF_WORLD * CURSOR_FALLOFF_WORLD

      for (let gx = startX; gx <= endX; gx += spacing) {
        for (let gy = startY; gy <= endY; gy += spacing) {
          let alpha = baseAlpha + pulse
          let dotR = DOT_BASE_RADIUS

          if (cursorWorld) {
            const dx = gx - cursorWorld.x
            const dy = gy - cursorWorld.y
            const dSq = dx * dx + dy * dy
            if (dSq < falloffSq) {
              const t = 1 - Math.sqrt(dSq) / CURSOR_FALLOFF_WORLD
              const gauss = t * t
              alpha += (DOT_PEAK_ALPHA - baseAlpha) * gauss
              dotR += (DOT_PEAK_RADIUS - DOT_BASE_RADIUS) * gauss
            }
          }

          for (let i = 0; i < dragHalos.length; i++) {
            const h = dragHalos[i]!
            const d = distanceFromRectEdge(gx, gy, h)
            if (d >= DRAG_HALO_FALLOFF_WORLD) continue
            // Inside the item bbox itself: still apply the boost so the dots beneath
            // the item glow too (they're occluded visually but help blend the halo).
            const t = 1 - d / DRAG_HALO_FALLOFF_WORLD
            const gauss = t * t
            alpha += DRAG_HALO_PEAK_ALPHA_BOOST * gauss
            dotR += DRAG_HALO_PEAK_RADIUS_BOOST * gauss
          }

          for (let i = 0; i < ripples.length; i++) {
            const ripple = ripples[i]!
            const age = now - ripple.startTime
            const ringRadius = age * RIPPLE_SPEED_PX_PER_MS
            // Ring expands outward from the item edge, so big items get a visible
            // ring around their silhouette throughout the ripple's life.
            const d = distanceFromRectEdge(gx, gy, ripple)
            const diff = Math.abs(d - ringRadius)
            if (diff > RIPPLE_WIDTH_WORLD) continue
            const ringStrength = (1 - diff / RIPPLE_WIDTH_WORLD) * (1 - age / RIPPLE_LIFETIME_MS)
            alpha += RIPPLE_PEAK_ALPHA * ringStrength
            dotR += RIPPLE_PEAK_RADIUS_BOOST * ringStrength
          }

          ctx.beginPath()
          ctx.arc(gx, gy, dotR, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(${rgb}, ${Math.min(alpha, 1)})`
          ctx.fill()
        }
      }

      raf = requestAnimationFrame(draw)
    }

    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0" aria-hidden />
}
